/**
 * Turn the guest who scanned a provider's discharge QR into a patient with an
 * account, keeping the instructions they are looking at.
 *
 * Same shape as upgradeVisitStartGuestToAccount, for the same reason: the
 * anonymous account is upgraded IN PLACE (linkWithCredential), so the uid never
 * changes and the instructions already under `users/{uid}/dischargeInstructions`
 * simply stay where they are. All that is left is telling the server to stop
 * expiring them (keepGuestDischargeInstructions), which clients cannot do
 * themselves because the rules make those docs server-write-only.
 *
 * ORDER MATTERS, as it does there: link, then the user document, then the keep.
 * Once the link succeeds the account exists, so nothing after it may throw except
 * the user doc (without which the patient is bounced between /onboarding and /auth).
 */

import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import firebaseApp, { db } from "services/firebase";
import { buildPatientUserDoc } from "features/visitStart/visitStartAccountUpgrade";
import { keepGuestDischargeInstructions } from "services/healthRecordsShareService";
import i18n from "i18n";

const friendlyAuthError = (err) => {
  switch (err?.code) {
    case "auth/email-already-in-use":
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return i18n.t("medications:dischargeHandoff.errors.emailInUse");
    case "auth/weak-password":
      return i18n.t("medications:dischargeHandoff.errors.weakPassword");
    case "auth/invalid-email":
      return i18n.t("medications:dischargeHandoff.errors.invalidEmail");
    case "auth/operation-not-allowed":
      return i18n.t("medications:dischargeHandoff.errors.signUpDisabled");
    case "auth/requires-recent-login":
      return i18n.t("medications:dischargeHandoff.errors.recentLogin");
    default:
      return err?.message || i18n.t("medications:dischargeHandoff.errors.generic");
  }
};

/**
 * The verification email links back to this discharge page (continueUrl), which
 * Verify.js follows. Without it the link dropped the patient on /onboarding, or
 * on the sign-in page whenever that browser had no session, with no way back.
 * Same fallback as AuthFlow: an unauthorized continue domain still gets a plain email.
 */
const sendVerification = async (user, handoffId) => {
  const settings = handoffId
    ? { url: `${window.location.origin}/discharge?h=${handoffId}`, handleCodeInApp: false }
    : undefined;
  try {
    await user.sendEmailVerification(settings);
  } catch (err) {
    if (
      settings &&
      (err?.code === "auth/unauthorized-continue-uri" || err?.code === "auth/invalid-continue-uri")
    ) {
      await user.sendEmailVerification();
      return;
    }
    throw err;
  }
};

/**
 * @param {{ email: string, password: string, handoffId?: string }} args
 * @returns {Promise<{ uid: string, kept: boolean, verificationSent: boolean }>}
 * @throws {Error} with a message written for the patient.
 */
export async function upgradeDischargeGuestToAccount({ email, password, handoffId } = {}) {
  const cleanEmail = String(email || "").trim();
  if (!cleanEmail || !password) {
    throw new Error(i18n.t("medications:dischargeHandoff.errors.missingFields"));
  }

  const current = firebaseApp.auth().currentUser;
  if (!current) {
    throw new Error(i18n.t("medications:dischargeHandoff.errors.expired"));
  }
  if (!current.isAnonymous) {
    throw new Error(i18n.t("medications:dischargeHandoff.errors.alreadySignedIn"));
  }

  let upgraded;
  try {
    const credential = firebase.auth.EmailAuthProvider.credential(cleanEmail, password);
    const result = await current.linkWithCredential(credential);
    upgraded = result?.user || firebaseApp.auth().currentUser;
  } catch (err) {
    throw new Error(friendlyAuthError(err));
  }

  const uid = upgraded?.uid;
  if (!uid) throw new Error(i18n.t("medications:dischargeHandoff.errors.generic"));

  try {
    await db
      .collection("users")
      .doc(uid)
      .set(buildPatientUserDoc({ uid, email: upgraded.email || cleanEmail }));
  } catch (err) {
    console.warn("[DischargeHandoff] user doc create failed", err);
    throw new Error(i18n.t("medications:dischargeHandoff.errors.setupIncomplete"));
  }

  // Best effort, and losing it loses nothing today: guestCleanup only deletes guest
  // copies while reviewing an account that is still anonymous, and this one no longer
  // is. The flags are cleared anyway so no future sweep can mistake them for unowned.
  let kept = false;
  try {
    await keepGuestDischargeInstructions();
    kept = true;
  } catch (err) {
    console.warn("[DischargeHandoff] keep failed", err);
  }

  let verificationSent = false;
  try {
    await sendVerification(upgraded, handoffId);
    verificationSent = true;
  } catch (err) {
    console.warn("[DischargeHandoff] verification email failed", err);
  }

  return { uid, kept, verificationSent };
}
